import React, { useCallback, useEffect, useState } from "react";
import { DarwinHeader, SortBy, SortOrder } from "build-ts/common";
import { ChatClientType } from "build-ts/chat_session";
import { Header } from "build-ts/metadata";
import { fetchListOfChatSessions, deleteChatSession, updateChatSessionHeaders } from "services/AgentChatService";
import { HeaderWithStats } from "services/MetadataServices";
import showToast from "hooks/useCustomToast";
import { useUser } from "context/UserContext";
import { useDisclosure } from "@chakra-ui/react";
import ConfirmationModal from "common/modals/ConfirmationModal";
import ChatSessionList from "modules/chat/components/ChatSessionList";

interface ChatSessionListContainerProps {
  analystsList: HeaderWithStats[];
}

const ChatSessionListContainer: React.FC<ChatSessionListContainerProps> = ({ analystsList }) => {
  const { userId } = useUser();

  // Data state
  const [chatSessions, setChatSessions] = useState<DarwinHeader[]>([]);
  const [totalChats, setTotalChats] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // UI interaction state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Inline rename state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  // Analyst filter state
  const [selectedAnalyst, setSelectedAnalyst] = useState<Header | null>(null);

  // Delete confirmation dialog
  // null → idle; "bulk" → bulk delete; string → single session id
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null | "bulk">(null);

  // Fetch all user chats on mount — no analyst filter; user can narrow down via the dropdown
  useEffect(() => {
    fetchChatSessions();
  }, []);

  const fetchChatSessions = async (
    search?: string,
    analystFilter?: Header | null,
    offset: number = 0,
    append: boolean = false
  ) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    try {
      // Build associatedEntityKey if analyst is selected
      const associatedEntityKey = analystFilter ? [`ANALYST::${analystFilter.id}`] : [];

      const result = await fetchListOfChatSessions(
        [ChatClientType.FRIDA],
        50,
        offset,
        search,
        undefined,
        associatedEntityKey,
        SortBy.LAST_MODIFIED_DATE,
        SortOrder.DESC,
        userId
      );

      if (append) {
        setChatSessions(prev => [...prev, ...result.headers]);
        // totalChats was already set to the server total on the initial fetch — don't increment it
      } else {
        setChatSessions(result.headers);
        setTotalChats(result.total ?? result.headers.length);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = useCallback(() => {
    fetchChatSessions(searchQuery || undefined, selectedAnalyst, chatSessions.length, true);
  }, [searchQuery, selectedAnalyst, chatSessions.length]);

  const handleRenameStart = useCallback((sessionId: string, name: string) => {
    setEditValue(name);
    setEditingId(sessionId);
  }, []);

  const handleRenameChange = useCallback((value: string) => {
    setEditValue(value);
  }, []);

  const handleRenameSubmit = useCallback(
    async (sessionId: string) => {
      if (!editValue.trim()) {
        setEditingId(null);
        return;
      }
      const success = await updateChatSessionHeaders([sessionId], editValue.trim(), undefined, false);
      if (success) {
        showToast("Chat renamed", "", "success");
        fetchChatSessions(searchQuery || undefined, selectedAnalyst);
      }
      setEditingId(null);
    },
    [editValue, searchQuery, selectedAnalyst]
  );

  const handleRenameCancel = useCallback(() => {
    setEditingId(null);
  }, []);

  const handleAnalystSelect = useCallback(
    (analyst: Header | null) => {
      setSelectedAnalyst(analyst);
      fetchChatSessions(searchQuery || undefined, analyst);
    },
    [searchQuery]
  );

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        fetchChatSessions(searchQuery || undefined, selectedAnalyst);
      }
    },
    [searchQuery, selectedAnalyst]
  );

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === chatSessions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(chatSessions.map(s => s.id!).filter(Boolean)));
    }
  }, [chatSessions, selectedIds.size]);

  const handleDeleteSelected = useCallback(() => {
    if (selectedIds.size === 0) return;
    setPendingDeleteId("bulk");
    onDeleteOpen();
  }, [selectedIds.size, onDeleteOpen]);

  const handleDeleteSingle = useCallback(
    (sessionId: string) => {
      setPendingDeleteId(sessionId);
      onDeleteOpen();
    },
    [onDeleteOpen]
  );

  const confirmDelete = useCallback(async () => {
    onDeleteClose();
    setIsDeleting(true);
    try {
      if (pendingDeleteId === "bulk") {
        const idsToDelete = Array.from(selectedIds);
        await Promise.all(idsToDelete.map(id => deleteChatSession(id)));
        const deletedIds = new Set(selectedIds);
        setChatSessions(prev => prev.filter(s => !deletedIds.has(s.id!)));
        setTotalChats(prev => prev - deletedIds.size);
        setSelectedIds(new Set());
      } else if (pendingDeleteId) {
        await deleteChatSession(pendingDeleteId);
        setChatSessions(prev => prev.filter(s => s.id !== pendingDeleteId));
        setTotalChats(prev => prev - 1);
      }
    } finally {
      setIsDeleting(false);
      setPendingDeleteId(null);
    }
  }, [pendingDeleteId, selectedIds, onDeleteClose]);

  const handleChatClick = useCallback((id: string) => {
    window.open(`/chat/${id}`, "_blank");
  }, []);

  const handleHoverEnter = useCallback((id: string) => {
    setHoveredId(id);
  }, []);

  const handleHoverLeave = useCallback(() => {
    setHoveredId(null);
  }, []);

  const isAllSelected = chatSessions.length > 0 && selectedIds.size === chatSessions.length;

  return (
    <>
      <ChatSessionList
        chatSessions={chatSessions}
        totalChats={totalChats}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        isDeleting={isDeleting}
        searchQuery={searchQuery}
        selectedIds={selectedIds}
        hoveredId={hoveredId}
        editingId={editingId}
        editValue={editValue}
        selectedAnalyst={selectedAnalyst}
        analystsList={analystsList}
        isAllSelected={isAllSelected}
        onSearchChange={handleSearchChange}
        onSearchKeyDown={handleSearchKeyDown}
        onToggleSelection={toggleSelection}
        onToggleSelectAll={toggleSelectAll}
        onDeleteSelected={handleDeleteSelected}
        onAnalystSelect={handleAnalystSelect}
        onChatClick={handleChatClick}
        onRenameStart={handleRenameStart}
        onRenameChange={handleRenameChange}
        onRenameSubmit={handleRenameSubmit}
        onRenameCancel={handleRenameCancel}
        onDeleteSingle={handleDeleteSingle}
        onLoadMore={handleLoadMore}
        onHoverEnter={handleHoverEnter}
        onHoverLeave={handleHoverLeave}
      />

      <ConfirmationModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={confirmDelete}
        title={
          pendingDeleteId === "bulk"
            ? `Delete ${selectedIds.size} chat${selectedIds.size > 1 ? "s" : ""}`
            : "Delete chat"
        }
        text={
          pendingDeleteId === "bulk"
            ? `Are you sure you want to delete ${selectedIds.size} selected chat${selectedIds.size > 1 ? "s" : ""}?` +
              "This cannot be undone."
            : "Are you sure you want to delete this chat? This cannot be undone."
        }
        confirmText="Delete"
        isLoading={isDeleting}
      />
    </>
  );
};

export default ChatSessionListContainer;
